// ─────────────────────────────────────────────────────────────────────────────
// dashboardService.js — Enterprise dashboard API calls
// ─────────────────────────────────────────────────────────────────────────────
import api from "./api";

/**
 * GET /dashboard
 * Returns KPIs + traffic_by_region + recent_alerts + recent_admin_actions
 */
export async function getDashboardMetrics() {
  return api.get("/dashboard");
}

/**
 * GET /dashboard/trends?from=ISO&to=ISO
 * Returns raw time-series rows for charts.
 * @param {Date} from
 * @param {Date} to
 */
export async function getDashboardTrends(from, to) {
  const params = new URLSearchParams();
  if (from) params.set("from", from.toISOString());
  if (to)   params.set("to",   to.toISOString());
  const qs = params.toString() ? `?${params}` : "";
  return api.get(`/dashboard/trends${qs}`);
}

/**
 * GET /dashboard/session-trend?from=ISO&to=ISO
 * Pre-aggregated daily active sessions — no frontend grouping needed.
 * Returns one { date, sessions } entry per calendar day (UTC), oldest → newest.
 * Days with no data are omitted; fill gaps with 0 if needed.
 * Defaults to last 7 days when from/to are omitted.
 * @param {Date} [from]
 * @param {Date} [to]
 * @returns {{ days: Array<{ date: string, sessions: number }>, count: number }}
 */
export async function getSessionTrend(from, to) {
  const params = new URLSearchParams();
  if (from) params.set("from", from.toISOString());
  if (to)   params.set("to",   to.toISOString());
  const qs = params.toString() ? `?${params}` : "";
  return api.get(`/dashboard/session-trend${qs}`);
}

/**
 * GET /activity/node-utilization?from=ISO&to=ISO
 * Fleet node utilization = avg load_percent across live hosts.
 * Returns { current: { value, delta_percent }, trend: [{ date, utilization }] }.
 *   current.value         — average utilization % right now
 *   current.delta_percent — vs one week ago (feed to formatDelta)
 *   trend                 — one entry per UTC day (oldest → newest); days with
 *                           no data are omitted. from/to bound the trend only
 *                           and default to the last 7 days.
 * @param {Date} [from]
 * @param {Date} [to]
 */
export async function getNodeUtilization(from, to) {
  const params = new URLSearchParams();
  if (from) params.set("from", from.toISOString());
  if (to)   params.set("to",   to.toISOString());
  const qs = params.toString() ? `?${params}` : "";
  return api.get(`/activity/node-utilization${qs}`);
}

/**
 * GET /activity/session-duration
 * Fleet-wide average WireGuard session length over the rolling last 7 days, plus
 * the signed hour change vs the prior 7 days. No params — the windows are fixed.
 * Returns { avg_session_duration_hours, delta_hours, this_week, previous_week }.
 *   avg_session_duration_hours — this week's average, hours (1 decimal)
 *   delta_hours                — this week − last week, signed, hours (1 decimal)
 * All-zero is a valid "no recent activity" result, not an error.
 */
export async function getSessionDuration() {
  return api.get("/activity/session-duration");
}

/**
 * GET /activity/trust-score
 * Fleet-wide average circuit trust score over the rolling last 7 days, plus the
 * signed point change vs the prior 7 days. No params — the windows are fixed.
 * Returns { trust_score, delta_points, this_week, previous_week }.
 *   trust_score  — this week's average, 0–100 score (NOT a percent), 1 decimal
 *   delta_points — this week − last week, signed, points (1 decimal)
 * All-zero is a valid "no active circuits" result, not an error.
 */
export async function getTrustScore() {
  return api.get("/activity/trust-score");
}

/**
 * GET /activity/route-success
 * Fleet-wide route/connection success rate over the rolling last 7 days, plus the
 * signed percentage-point change vs the prior 7 days. No params — windows fixed.
 * Returns { route_success_rate, delta_points, this_week, previous_week }.
 *   route_success_rate — this week's rate, already a PERCENT (1 decimal); don't ×100
 *   delta_points       — this week − last week, signed, percentage points (1 decimal)
 * All-zero is a valid "no recent metrics" result, not an error.
 */
export async function getRouteSuccess() {
  return api.get("/activity/route-success");
}

/**
 * GET /activity/daily-active-users
 * Company-scoped distinct active users for the LAST COMPLETE UTC day, plus the
 * signed % change vs the day before. No params. Returns
 * { daily_active_users, delta_percent, date, current, previous }.
 *   daily_active_users — integer, last complete UTC day's distinct active users
 *   delta_percent      — signed % vs the prior day (feed to formatDelta)
 * All-zero is a valid "no activity" result, not an error.
 */
export async function getDailyActiveUsers() {
  return api.get("/activity/daily-active-users");
}

/**
 * GET /activity/user-activity
 * Company-scoped distinct active users per UTC day over a rolling 7-day window.
 * Returns { trend: [{ date, active_users }] } — always exactly 7 points, oldest →
 * newest, contiguous (zero days included). The last point is today (in progress).
 * All-zero is a valid "no activity" result, not an error.
 */
export async function getUserActivity() {
  return api.get("/activity/user-activity");
}

/**
 * GET /activity/node-usage-by-region
 * Per-region average node utilization (load_percent) over the last 7 days.
 * Returns { regions: [{ region, utilization, nodes }] }, sorted by utilization desc.
 *   regions[].region      — region name ("Unknown" if blank)
 *   regions[].utilization — 7-day mean utilization, 0–100 (1 decimal)
 *   regions[].nodes       — distinct physical hosts seen in that region
 */
export async function getNodeUsageByRegion() {
  return api.get("/activity/node-usage-by-region");
}

/**
 * GET /activity/top-nodes?limit=N
 * Top N live nodes ranked by active_circuits.
 * Returns { nodes: [{ node_id, region, trust_score, active_circuits, load_percent }], limit, count }.
 * Fields are raw (unformatted) — the UI formats trust_score/load_percent for display.
 * @param {number} [limit=4]
 */
export async function getTopNodes(limit = 4) {
  const qs = limit != null ? `?limit=${limit}` : "";
  return api.get(`/activity/top-nodes${qs}`);
}

/**
 * Fetches the session history aggregate for each of the last 7 days (UTC).
 * Calls GET /enterprise-nodes/sessions/history?date=YYYY-MM-DD in parallel.
 * (Was /nodes/sessions/history — that surface was removed.)
 */
export async function getSessionHistory7Days() {
  const dates = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
    dates.push(d.toISOString().split("T")[0]);
  }

  const promises = dates.map(async (date) => {
    try {
      const res = await api.get(`/enterprise-nodes/sessions/history?date=${date}`);
      return res; // { date, total_sessions, total_duration_seconds }
    } catch (err) {
      console.warn(`[dashboardService] Failed to fetch session history for ${date}:`, err.message);
      return { date, total_sessions: 0, total_duration_seconds: 0 };
    }
  });

  return Promise.all(promises);
}

// ── Date range helpers ────────────────────────────────────────────────────────

export function rangeToFromDate(label) {
  const now = new Date();
  switch (label) {
    case "Last 7 Days":  return new Date(now - 7  * 24 * 60 * 60 * 1000);
    case "Last 30 Days": return new Date(now - 30 * 24 * 60 * 60 * 1000);
    default:             return new Date(now - 24 * 60 * 60 * 1000); // Last 24 Hours
  }
}

// ── Data mapping helpers ──────────────────────────────────────────────────────

const REGION_COLORS = ["#00e87a", "#3b82f6", "#a855f7", "#ffc542", "#ff4560", "#00c8e8"];

/**
 * Converts traffic_by_region array to donut chart format.
 */
export function buildDonutData(trafficByRegion = []) {
  const total = trafficByRegion.reduce((s, r) => s + (r.total_bytes ?? 0), 0);
  if (!total || !trafficByRegion.length) return [];
  return trafficByRegion.slice(0, 6).map((r, i) => ({
    label: r.region || r.country || "Unknown",
    pct:   Math.round((r.total_bytes / total) * 100),
    color: REGION_COLORS[i] || "#666",
  }));
}

/**
 * Groups trends rows by day, summing active_circuits across all nodes.
 * Returns { labels: string[], sessions: number[] } — 7 data points max.
 */
export function buildActivityData(trends = [], rangeLabel = "Last 7 Days") {
  if (!trends.length) return null;

  // Determine bucket size based on range
  const bucketMs = rangeLabel === "Last 24 Hours" ? 60 * 60 * 1000        // 1h buckets
                 : rangeLabel === "Last 30 Days"  ? 3 * 24 * 60 * 60 * 1000 // 3d buckets
                 : 24 * 60 * 60 * 1000;                                    // 1d buckets (7d)

  const buckets = new Map();
  for (const row of trends) {
    const ts    = new Date(row.fetched_at).getTime();
    const key   = Math.floor(ts / bucketMs) * bucketMs;
    const entry = buckets.get(key) ?? { sessions: 0, count: 0 };
    entry.sessions += (row.active_circuits ?? 0);
    entry.count++;
    buckets.set(key, entry);
  }

  const sorted = [...buckets.entries()].sort((a, b) => a[0] - b[0]).slice(-7);

  const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const labels   = sorted.map(([ts]) => {
    const d = new Date(ts);
    return rangeLabel === "Last 24 Hours"
      ? d.getHours().toString().padStart(2, "0") + ":00"
      : DAY_LABELS[d.getDay()];
  });
  const sessions = sorted.map(([, e]) => e.sessions);

  return { labels, sessions };
}

/**
 * Groups trends rows into range-appropriate buckets, averaging entry_throughput_bps.
 * Bucket size scales with the selected range (same convention as buildActivityData):
 *   "Last 24 Hours" -> 1h buckets, up to 24, hour-of-day labels ("00".."23")
 *   "Last 7 Days"   -> 1d buckets, up to 7,  day-name labels
 *   "Last 30 Days"  -> 3d buckets, up to 10, day-name labels
 * BandwidthChart does its own bps -> Kbps/Mbps/Gbps/Tbps unit scaling, so values
 * here stay in raw bps. Returns { labels, values, isHourly } or null.
 */
export function buildBandwidthData(trends = [], rangeLabel = "Last 24 Hours") {
  if (!trends.length) return null;

  const isHourly  = rangeLabel === "Last 24 Hours";
  const bucketMs  = isHourly                        ? 60 * 60 * 1000
                  : rangeLabel === "Last 30 Days"   ? 3 * 24 * 60 * 60 * 1000
                  :                                    24 * 60 * 60 * 1000;
  const maxBuckets = isHourly ? 24 : rangeLabel === "Last 30 Days" ? 10 : 7;

  const buckets = new Map();
  for (const row of trends) {
    const ts    = new Date(row.fetched_at).getTime();
    const key   = Math.floor(ts / bucketMs) * bucketMs;
    const entry = buckets.get(key) ?? { sum: 0, count: 0 };
    entry.sum   += Number(row.entry_throughput_bps ?? 0);
    entry.count++;
    buckets.set(key, entry);
  }

  const sorted = [...buckets.entries()].sort((a, b) => a[0] - b[0]).slice(-maxBuckets);

  const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const labels = sorted.map(([ts]) => {
    const d = new Date(ts);
    return isHourly ? d.getHours().toString().padStart(2, "0") : DAY_LABELS[d.getDay()];
  });
  const values = sorted.map(([, e]) => Math.round((e.sum / e.count) || 0));

  return { labels, values, isHourly };
}

/**
 * Maps recent_alerts from backend to the UI alert format.
 */
export function mapAlerts(recentAlerts = []) {
  const SEV_COLOR = { critical: "#ff4560", warn: "#ffc542", info: "#00e87a" };

  return recentAlerts.slice(0, 5).map(a => {
    const firstAlert = Array.isArray(a.alerts) ? a.alerts[0] : "Alert";
    const sev = firstAlert?.toLowerCase().includes("critical") ? "critical"
              : firstAlert?.toLowerCase().includes("warn")     ? "warn"
              : "info";
    return {
      color: SEV_COLOR[sev],
      label: firstAlert || "Node Alert",
      sub:   `${a.region ?? ""} Node (${a.ip ?? a.node_id ?? "unknown"})`,
      time:  formatRelative(a.fetched_at),
      sev,
    };
  });
}

/**
 * Maps recent_admin_actions from backend to the UI admin action format.
 */
export function mapAdminActions(recentActions = []) {
  return recentActions.slice(0, 3).map(a => ({
    action:      a.action,
    text:        a.description || a.action,
    who:         a.metadata?.userName ?? a.userId?.slice(0, 8) ?? "System",
    when:        formatRelative(a.createdAt),
  }));
}

/**
 * Formats a delta_percent into "+12.5%" or "-2.4%" + trend direction.
 */
export function formatDelta(deltaPercent) {
  const pct = deltaPercent ?? 0;
  return {
    trend:    pct >= 0 ? "up" : "down",
    trendVal: `${pct >= 0 ? "+" : ""}${pct}%`,
  };
}

// ── Relative time ─────────────────────────────────────────────────────────────

function formatRelative(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
